import { gql } from "graphql-request";
import { GetServerSideProps } from "next";
import Ad from "src/components/ads/Ad";
import SContentWrapper from "src/components/contentwrapper/ContentWrapper";
import withWatchlist from "src/components/filmwebid/withWatchlist";
import PersonSeo from "src/components/seo/PersonSeo";
import StreamingTitleWithProviders from "src/components/streaming/streamingtitlewithproviders/StreamingTitleWithProviders";
import AdWrapper, { AdUnit } from "src/lib/contexts/AdContext";
import { WatchableContent } from "src/lib/movieinfo/movieinfotypes";
import { useOverriddenAdunits } from "src/lib/server/ads";
import { setDefaultCacheHeaders } from "src/lib/server/cache";
import { filminfoQuery } from "src/lib/server/filminfo";
import { ICommonProps, getServerData } from "src/lib/server/sanity";
import styled from "src/lib/styles/css";

//#region [Other] getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {

    setDefaultCacheHeaders(context);
    let personName = context.params?.personName;

    if (!personName) {
        return { notFound: true };
    }

    personName = decodeURI(personName as string).replace(/_/g, " ");

    const filmographyResult = await filminfoQuery(QUERY, { searchText: personName, searchForActorAndDirectorOnly: true, orderByProductionYearDescending: true });
    const result = filmographyResult?.searchForWatchableContent;
    const filmography: IFilmography = {
        top: (result?.slice(0, 4) ?? null) as (WatchableContent[] | null),
        mid1: (result?.slice(4, 12) ?? null) as (WatchableContent[] | null),
        mid2: (result?.slice(12, 20) ?? null) as (WatchableContent[] | null),
        rest: (result?.slice(20) ?? null) as (WatchableContent[] | null)
    };

    return {
        props: {
            name: personName,
            filmography: filmography
        }
    }

});
//#endregion

//#region [Props]
export type PersonProps = {
    name: string;
    filmography: IFilmography;
} & ICommonProps;

interface IFilmography {
    top: WatchableContent[] | null;
    mid1: WatchableContent[] | null;
    mid2: WatchableContent[] | null;
    rest: WatchableContent[] | null;
}

//#endregion

//#region [Component]
export default function Person({ name, filmography, adUnits }: PersonProps) {
    const hasFilmography = (filmography.top?.length ?? 0) > 0;
    const finalAdUnits = useOverriddenAdunits(ADS, adUnits);

    return <SContentWrapper as="section">
        <PersonSeo name={name} />
        <AdWrapper ads={finalAdUnits}>
            <SName>{name}</SName>
            <SElementSpacer>
                {!hasFilmography && <SNoFilmography>Fant ingen filmografi</SNoFilmography>}
                {hasFilmography && <>
                    <SWatchableList>
                        {filmography.top!.map(content => <WLStreamingTitleWithProviders
                            key={content.movieId ?? content.streamingContentId}
                            data={content}
                        />)}
                    </SWatchableList>
                </>}

                <Ad targetId="SG3_top_filmografi" />

                {hasFilmography && <>
                    {(filmography.mid1?.length ?? 0) > 0 && <>
                        <SWatchableList>
                            {filmography.mid1!.map(content => <WLStreamingTitleWithProviders
                                key={content.movieId ?? content.streamingContentId}
                                data={content}
                            />)}
                        </SWatchableList>
                        <Ad targetId="SG3_midt1_filmografi" />
                    </>}

                    {(filmography.mid2?.length ?? 0) > 0 && <>
                        <SWatchableList>
                            {filmography.mid2!.map(content => <WLStreamingTitleWithProviders
                                key={content.movieId ?? content.streamingContentId}
                                data={content}
                            />)}
                        </SWatchableList>
                        <Ad targetId="SG3_midt2_filmografi" />
                    </>}

                    {(filmography.rest?.length ?? 0) > 0 && <>
                        <SWatchableList>
                            {filmography.rest!.map(content => <WLStreamingTitleWithProviders
                                key={content.movieId ?? content.streamingContentId}
                                data={content}
                            />)}
                        </SWatchableList>
                        <Ad targetId="SG3_bottom_filmografi" />
                    </>}
                </>}
            </SElementSpacer>


        </AdWrapper>
    </SContentWrapper>
}
//#endregion

const WLStreamingTitleWithProviders = withWatchlist(StreamingTitleWithProviders, "data");



//#region [Styles]
const SName = styled.h1`
	text-align: center;
	font-size: 28px;
	margin: 10px 0;
	color: var(--textcolor);
`;

const SNoFilmography = styled.p`
	text-align: center;
	color: var(--textcolor);
`;

const SElementSpacer = styled.div`
	margin-bottom: 35px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	@media (min-width: 550px) {
		gap: 35px;
	}

`;

const SWatchableList = styled.div`
	margin: 0 10px;
	box-sizing: content-box;
	gap: 10px;
	display: flex;
	flex-direction: column;

	@media (min-width: 550px) {
		display: grid;
		grid-gap: 4%;
		gap: 50px;
		grid-template-columns: repeat(4, calc((100% - 150px) / 4));
		grid-auto-rows: auto;
	}
`;

//#endregion


const ADS: AdUnit[] = [

    { auId: '0000000000137971', auW: '100%', auH: '300', targetId: 'SG3_top_filmografi' },
    { auId: '00000000001379a1', auW: '100%', auH: '300', targetId: 'SG3_midt1_filmografi' },
    { auId: '00000000001379a4', auW: '100%', auH: '300', targetId: 'SG3_midt2_filmografi' },
    { auId: '00000000001379a8', auW: '100%', auH: '300', targetId: 'SG3_bottom_filmografi' },
];

const QUERY = gql`
query(
	$searchText: String
	$searchForActorAndDirectorOnly: Boolean
	$orderByProductionYearDescending: Boolean
  	)
{
		searchForWatchableContent(
	  		searchText: $searchText
	  		searchForActorAndDirectorOnly: $searchForActorAndDirectorOnly
	  		orderByProductionYearDescending: $orderByProductionYearDescending
	  		maxNumItems: 1000
		) {
			__typename
			movieId
			streamingContentId
			title
			isCinemaRelevant
	  		hasCinemaShows
	  		isSeries
	  		productionYear
	  		imagesPosterStreaming {
				height
				width
				url
	  		}
	  		sanityImagePosterUrl
	  		streamingProviderContents {
				__typename
				id
				title
				url
				provider {
					__typename
		  			id
		  			name
		  			logo
				}
	  	}
	}
}
`;